import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import {
  BusinessAutomationActionTypeEnum,
  type DateBasedTriggerMetadata,
  type EventBasedTriggerMetadata,
} from '@reach/shared-types/business-automation';
import type { SenderDetailsUpdate } from '@reach/shared-types/schema-mappings';
import { useEffect, useMemo, useState } from 'react';
import type { AutomationTemplate } from '../automations-tab/templates.types';
import {
  type TenantSelection,
  tenantSelectionsDiffer,
  toTemplateTenantSelection,
} from '../automations-tab/templates.types';
import { checkActionNeedsSetup } from './actionSetupValidation';
import { actionTypeDefaults } from './actionTypeDefaults';
import { getTemplateSegmentUsage, hasRejectedSegmentReuse } from './segment-picker/segment-usage';

type TriggerMetadata = EventBasedTriggerMetadata | DateBasedTriggerMetadata;

type EditorContents = Omit<AutomationTemplateContents, 'triggerMetadata'> & {
  triggerMetadata: TriggerMetadata;
};

interface EditorState {
  localContents: EditorContents;
  tenantSelection: TenantSelection;
  isDirty: boolean;
  dirtySteps: {
    trigger: boolean;
    actions: Set<number>;
    settings: boolean;
    tenantSelection: boolean;
  };
  hasReorderedOrAdded: boolean;
  hasStepsNeedingSetup: boolean;
  hasDuplicateSegments: boolean;
}

function deepEqual<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

type RestoreActionWithPreservedContent = (
  currentActionMap: Map<string | undefined, AutomationTemplateContents['actionData'][0]>,
  cleanAction: AutomationTemplateContents['actionData'][0],
  index: number,
) => AutomationTemplateContents['actionData'][0];

const restoreActionWithPreservedContent: RestoreActionWithPreservedContent = (
  currentActionMap,
  cleanAction,
  index,
) => {
  const currentAction = cleanAction.templateActionId
    ? currentActionMap.get(cleanAction.templateActionId)
    : undefined;

  const restoredAction = currentAction || cleanAction;
  return {
    ...restoredAction,
    index,
  };
};

function markSendCommunicationActionsDirty(
  actions: AutomationTemplateContents['actionData'],
  dirtyActionIndices: Set<number>,
): void {
  for (let i = 0; i < actions.length; i++) {
    if (actions[i].type === BusinessAutomationActionTypeEnum.SEND_COMMUNICATION) {
      dirtyActionIndices.add(i);
    }
  }
}

interface SettingsUpdate {
  name: string;
  description: string;
  cooldownSeconds: number;
}

interface UseAutomationEditorReturn {
  state: EditorState;
  template: AutomationTemplate;
  updateTrigger: (metadata: TriggerMetadata) => void;
  updateAudience: (includeSegmentDefaults: AutomationTemplateContents['includeSegmentDefaults']) => void;
  updateExclusions: (excludeSegmentDefaults: string[]) => void;
  undoTrigger: () => void;
  updateSettings: (settings: SettingsUpdate) => void;
  updateUsePointOfContactSender: (value: boolean) => void;
  updatePointOfContactSender: (update: SenderDetailsUpdate) => void;
  undoSettings: () => void;
  addAction: (type: string, index: number) => void;
  updateAction: (index: number, data: Partial<AutomationTemplateContents['actionData'][0]>) => void;
  undoAction: (index: number) => void;
  deleteAction: (index: number) => void;
  reorderActions: (fromIndex: number, toIndex: number) => void;
  undoReorder: () => void;
  updateTenantSelection: (selection: TenantSelection) => void;
  undoTenantSelection: () => void;
  markClean: () => void;
  stripoIsOpen: boolean;
  openStripo: () => void;
  closeStripo: () => void;
}

function computeContentsDirtyState(
  contents: AutomationTemplateContents,
  savedContents: AutomationTemplateContents,
) {
  const triggerMetadataIsDirty = !deepEqual(contents.triggerMetadata, savedContents.triggerMetadata);
  const audienceIsDirty =
    !deepEqual(contents.includeSegmentDefaults, savedContents.includeSegmentDefaults) ||
    !deepEqual(contents.excludeSegmentDefaults, savedContents.excludeSegmentDefaults);
  // Audience is edited inside the trigger dialog, so it rolls up into the trigger step's dirty state.
  const triggerIsDirty = triggerMetadataIsDirty || audienceIsDirty;

  let cooldownChanged = false;
  if (
    contents.triggerMetadata?.triggerType === 'trigger_based' &&
    savedContents.triggerMetadata?.triggerType === 'trigger_based'
  ) {
    cooldownChanged =
      contents.triggerMetadata.cooldownSeconds !== savedContents.triggerMetadata.cooldownSeconds;
  }

  const usePointOfContactSenderDirty =
    !!contents.usePointOfContactSender !== !!savedContents.usePointOfContactSender;
  const senderProfileIdDirty =
    (contents.senderProfileId ?? null) !== (savedContents.senderProfileId ?? null);
  const pointOfContactSenderDirty = usePointOfContactSenderDirty || senderProfileIdDirty;

  const settingsAreDirty =
    contents.name !== savedContents.name ||
    contents.description !== savedContents.description ||
    cooldownChanged ||
    pointOfContactSenderDirty;

  const currentActions = contents.actionData || [];
  const savedActions = savedContents.actionData || [];

  const hasReorderedOrAdded =
    currentActions.length !== savedActions.length ||
    currentActions.some(
      (action, index) =>
        !savedActions[index] || action.templateActionId !== savedActions[index].templateActionId,
    );

  const dirtyActionIndices = new Set<number>();
  const savedActionMap = new Map((savedActions || []).map(action => [action.templateActionId, action]));

  const compareActionContent = (
    current: (typeof currentActions)[0],
    saved: (typeof savedActions)[0],
  ): boolean => {
    const { index: _currentIndex, ...currentContent } = current;
    const { index: _savedIndex, ...savedContent } = saved;
    return deepEqual(currentContent, savedContent);
  };

  for (let i = 0; i < currentActions.length; i++) {
    const currentAction = currentActions[i];
    const savedAction = savedActionMap.get(currentAction.templateActionId);

    if (savedAction && !compareActionContent(currentAction, savedAction)) {
      dirtyActionIndices.add(i);
    }
  }

  if (pointOfContactSenderDirty) {
    markSendCommunicationActionsDirty(currentActions, dirtyActionIndices);
  }

  return {
    triggerIsDirty,
    settingsAreDirty,
    dirtyActionIndices,
    hasReorderedOrAdded,
  };
}

export function useAutomationEditor(template: AutomationTemplate): UseAutomationEditorReturn {
  const [localContents, setLocalContents] = useState<AutomationTemplateContents>(template.contents);
  const [stripoIsOpen, setStripoIsOpen] = useState(false);
  const openStripo = () => setStripoIsOpen(true);
  const closeStripo = () => setStripoIsOpen(false);
  const [savedContents, setSavedContents] = useState<AutomationTemplateContents>(template.contents);

  const [localTenantSelection, setLocalTenantSelection] = useState<TenantSelection>(() =>
    toTemplateTenantSelection(template.tenants),
  );
  const [savedTenantSelection, setSavedTenantSelection] = useState<TenantSelection>(() =>
    toTemplateTenantSelection(template.tenants),
  );

  useEffect(() => {
    setSavedContents(template.contents);
    setLocalContents(template.contents);
  }, [template.contents]);

  useEffect(() => {
    const ts = toTemplateTenantSelection(template.tenants);
    setLocalTenantSelection(ts);
    setSavedTenantSelection(ts);
  }, [template.tenants]);

  const { triggerIsDirty, settingsAreDirty, dirtyActionIndices, hasReorderedOrAdded } = useMemo(
    () => computeContentsDirtyState(localContents, savedContents),
    [localContents, savedContents],
  );

  const state: EditorState = useMemo(() => {
    const tenantSelectionDirty = tenantSelectionsDiffer(localTenantSelection, savedTenantSelection);
    const hasStepsNeedingSetup = (localContents.actionData || []).some(action =>
      checkActionNeedsSetup(action),
    );
    const hasDuplicateSegments = hasRejectedSegmentReuse(getTemplateSegmentUsage(localContents));
    const isDirty =
      triggerIsDirty ||
      settingsAreDirty ||
      dirtyActionIndices.size > 0 ||
      hasReorderedOrAdded ||
      tenantSelectionDirty;

    return {
      localContents: localContents as EditorContents,
      tenantSelection: localTenantSelection,
      isDirty,
      dirtySteps: {
        trigger: triggerIsDirty,
        actions: dirtyActionIndices,
        settings: settingsAreDirty,
        tenantSelection: tenantSelectionDirty,
      },
      hasReorderedOrAdded,
      hasStepsNeedingSetup,
      hasDuplicateSegments,
    };
  }, [
    localContents,
    savedTenantSelection,
    localTenantSelection,
    triggerIsDirty,
    settingsAreDirty,
    dirtyActionIndices,
    hasReorderedOrAdded,
  ]);

  const updateTrigger = (metadata: TriggerMetadata) => {
    setLocalContents(prev => ({
      ...prev,
      triggerMetadata: metadata,
    }));
  };

  const updateAudience = (
    includeSegmentDefaults: AutomationTemplateContents['includeSegmentDefaults'],
  ) => {
    // Single-select replace: always set the whole array, never append.
    setLocalContents(prev => ({
      ...prev,
      includeSegmentDefaults,
    }));
  };

  const updateExclusions = (excludeSegmentDefaults: string[]) => {
    setLocalContents(prev => ({
      ...prev,
      excludeSegmentDefaults,
    }));
  };

  const undoTrigger = () => {
    setLocalContents(prev => ({
      ...prev,
      triggerMetadata: savedContents.triggerMetadata,
      includeSegmentDefaults: savedContents.includeSegmentDefaults,
      excludeSegmentDefaults: savedContents.excludeSegmentDefaults,
    }));
  };

  const updateSettings = ({ name, description, cooldownSeconds }: SettingsUpdate) => {
    setLocalContents(prev => {
      let triggerMetadata = prev.triggerMetadata;
      if (triggerMetadata?.triggerType === 'trigger_based') {
        triggerMetadata = {
          ...triggerMetadata,
          cooldownSeconds,
        };
      }

      return {
        ...prev,
        name,
        description,
        triggerMetadata,
      };
    });
  };

  const updateUsePointOfContactSender = (value: boolean) => {
    setLocalContents(prev => ({
      ...prev,
      usePointOfContactSender: value,
    }));
  };

  const updatePointOfContactSender = (update: SenderDetailsUpdate) => {
    const { senderProfileId, usePointOfContactSender } = update;
    setLocalContents(prev => ({
      ...prev,
      senderProfileId,
      usePointOfContactSender,
    }));
  };

  const undoSettings = () => {
    setLocalContents(prev => ({
      ...prev,
      name: savedContents.name,
      description: savedContents.description,
      triggerMetadata: savedContents.triggerMetadata,
    }));
  };

  const addAction = (type: string, index: number) => {
    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      const templateActionId = crypto.randomUUID();

      const actionDefaultData = actionTypeDefaults[type];
      if (!actionDefaultData) {
        return prev;
      }

      const newAction = {
        type: type as AutomationTemplateContents['actionData'][0]['type'],
        templateActionId,
        index,
        ...actionDefaultData,
      } as AutomationTemplateContents['actionData'][0];

      // Insert the new action at the specified index
      newActionData.splice(index, 0, newAction);

      // Update indices for all actions after the inserted one
      for (let i = index + 1; i < newActionData.length; i++) {
        newActionData[i] = {
          ...newActionData[i],
          index: i,
        };
      }

      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const updateAction = (index: number, data: Partial<AutomationTemplateContents['actionData'][0]>) => {
    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      if (newActionData[index]) {
        newActionData[index] = {
          ...newActionData[index],
          ...data,
        } as AutomationTemplateContents['actionData'][0];
      }
      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const undoAction = (index: number) => {
    const savedAction = savedContents.actionData?.[index];
    if (!savedAction) return;

    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      newActionData[index] = savedAction;
      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const deleteAction = (index: number) => {
    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      newActionData.splice(index, 1);
      // Update indices for all actions after the deleted one
      for (let i = index; i < newActionData.length; i++) {
        newActionData[i] = {
          ...newActionData[i],
          index: i,
        };
      }
      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const reorderActions = (fromIndex: number, toIndex: number) => {
    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      const [removed] = newActionData.splice(fromIndex, 1);
      newActionData.splice(toIndex, 0, removed);
      // Update indices for all actions to ensure they're correct
      for (let i = 0; i < newActionData.length; i++) {
        newActionData[i] = {
          ...newActionData[i],
          index: i,
        };
      }
      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const undoReorder = () => {
    const savedActions = savedContents.actionData || [];

    setLocalContents(prev => {
      const currentActions = prev.actionData || [];
      const currentActionMap = new Map(currentActions.map(action => [action.templateActionId, action]));
      const restorePreservedAction = restoreActionWithPreservedContent.bind(null, currentActionMap);

      return {
        ...prev,
        actionData: savedActions.map(restorePreservedAction),
      };
    });
  };

  const updateTenantSelection = setLocalTenantSelection;

  const undoTenantSelection = () => {
    setLocalTenantSelection(savedTenantSelection);
  };

  const markClean = () => {
    setSavedContents(localContents);
    setSavedTenantSelection(localTenantSelection);
  };

  return {
    state,
    template,
    updateTrigger,
    updateAudience,
    updateExclusions,
    undoTrigger,
    updateSettings,
    updateUsePointOfContactSender,
    updatePointOfContactSender,
    undoSettings,
    addAction,
    updateAction,
    undoAction,
    deleteAction,
    reorderActions,
    undoReorder,
    updateTenantSelection,
    undoTenantSelection,
    markClean,
    stripoIsOpen,
    openStripo,
    closeStripo,
  };
}
